import React, { useContext, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { createContext } from 'react';
import { OttoTracker } from '@televet/tracking-sdk';
import useClinicUser from 'shared/hooks/useClinicUser';
import { commitInfo } from 'commitInfo';
import { env } from 'env';
import { AppInfo, EventData } from '@televet/tracking-sdk/types';
import { EventName } from './EventNames';

const isProduction = env.REACT_APP_PRODUCTION === 'true';

export interface ITrackingContext {
  trackEvent: (eventName: EventName, eventData?: EventData) => void;
  logRocketSessionUrl?: string;
}

const TrackingContext = createContext<ITrackingContext>({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trackEvent: (eventName: EventName, eventData?: EventData): void => {
    throw new Error('trackEvent: (eventName: EventName, eventData?: EventData): void not implemented');
  },
});

export const useTracking = (): ITrackingContext => useContext<ITrackingContext>(TrackingContext);

interface TrackingProviderProps {
  children: ReactNode;
}

export const appInfo: AppInfo = {
  version: parseInt(`${commitInfo.commitNumber}`) / 100,
  name: 'Clinic Web',
  ...commitInfo,
};

const TrackingProvider = ({ children }: TrackingProviderProps): JSX.Element => {
  const { clinicUser, currentClinic } = useClinicUser();

  const tracker = useMemo(() => {
    return new OttoTracker({
      isProduction,
      isDebugEnabled: !isProduction,
    });
  }, []);

  // Initialize tracking when the component mounts
  useEffect(() => {
    if (!tracker) return;

    tracker?.initializeTracking({
      appVersion: appInfo.version.toString(),
      appName: appInfo.name,
      tokens: {
        GA: env.REACT_APP_GA4_ID,
        Logrocket: env.REACT_APP_LOG_ROCKET_APP_ID,
        Mixpanel: isProduction ? env.REACT_APP_PROD_MIXPANEL_TOKEN : undefined,
        CloudWatchRum: env.REACT_APP_CLOUDWATCH_RUM_APP_ID,
      },
      gaCustomDimensions: {
        dimension1: 'clinicId',
      },
      cloudwatchRumConfig: {
        identityPoolId: env.REACT_APP_CLOUDWATCH_RUM_IDENTITY_POOL_ID,
        endpoint: env.REACT_APP_CLOUDWATCH_RUM_ENDPOINT,
      },
      awsRegion: env.REACT_APP_CLOUDWATCH_RUM_REGION,
    });
  }, [tracker]);

  // Identify new user when user changes
  useEffect(() => {
    if (!clinicUser?.id || !currentClinic?.id || !tracker) return;
    tracker?.identifyUser({
      clinicInfo: {
        id: currentClinic?.id || '',
        name: currentClinic?.name,
      },
      appInfo: {
        name: 'Clinic Web',
        version: appInfo.version,
        commitNumber: appInfo.commitNumber,
        commitHash: appInfo.commitHash,
      },
      userInfo: {
        id: clinicUser?.id,
        email: clinicUser?.email,
        type: clinicUser?.type,
        name: `${clinicUser?.firstName} ${clinicUser?.lastName}`,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicUser?.id, tracker]);

  useEffect(() => {
    console.log('App Info:', appInfo);
  }, []);

  const trackEventCallback = useCallback(
    (eventName: EventName, eventData?: EventData): void => {
      tracker?.trackEvent(eventName, eventData);
    },
    [tracker],
  );

  return (
    <TrackingContext.Provider
      value={{
        trackEvent: trackEventCallback,
        logRocketSessionUrl: tracker.logRocketSessionUrl,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};

export default TrackingProvider;
